import { z } from 'zod';

import type { BinaryAnswer } from '@/constants/answers';
import {
  type ActiveEntityCriterion,
  BeneficialOwnerType,
  CONTROLLING_PERSON_TYPES,
  ControllingPersonType,
  EntityClassification,
  ExcludedPersonType,
  NoTinReason,
} from '@/constants/institution';
import {
  type InstitutionPerson,
  institutionDetailsPersonApiToInstitutionPerson,
  institutionPersonSchema,
  institutionPersonToInstitutionDetailsPersonApi,
} from '@/features/institution/schemas/person';
import i18n from '@/lib/i18n';
import { getCountryIsEu, mainstreamCountrySchema } from '@/services/api/mainstream';
import {
  type InstitutionDetailsV2ControllingPerson,
  type InstitutionDetailsV2EntityTaxResidency,
  type InstitutionDetailsV2Partial,
  type InstitutionDetailsV2TaxSelfCertification,
  institutionDetailsV2ActiveEntityCriterionSchema,
  institutionDetailsV2ControllingPersonSchema,
  institutionDetailsV2EntityTinSchema,
  institutionDetailsV2ListedEntityNameSchema,
  institutionDetailsV2ListedMarketSchema,
  institutionDetailsV2NoTinExplanationSchema,
  institutionDetailsV2TaxSelfCertificationSchema,
} from '@/services/api/profile';
import { addFieldIssue } from '@/utils/field-issues';
import { EMPTY_PHONE_NUMBER } from '@/utils/phone-number';

export const TAX_RESIDENCIES_MAX = 5;

export const CONTROLLING_PERSONS_MAX = 20;

type InstitutionEntityTaxResidency = {
  jurisdiction: string;
  hasNoTin: boolean;
  tin: string;
  noTinReason?: NoTinReason;
  noTinExplanation: string;
};

type InstitutionControllingPerson = InstitutionPerson & {
  controllingPersonTypes: ControllingPersonType[];
  isPep?: BinaryAnswer;
};

export type InstitutionTaxSelfCertification = {
  noTaxResidence?: BinaryAnswer;
  taxResidencies: InstitutionEntityTaxResidency[];
  placeOfEffectiveManagement: string;
  excludedPersonType?: ExcludedPersonType;
  listedEntityName: string;
  listedMarket: string;
  entityClassification?: EntityClassification;
  activeEntityCriterion?: ActiveEntityCriterion;
  controllingPersons: InstitutionControllingPerson[];
  declaration: {
    accurate: boolean;
    consentToReporting: boolean;
    undertakeToNotifyChanges: boolean;
  };
  signatory: {
    partyId?: string;
    fullName: string;
    capacity: string;
  };
};

export type InstitutionTaxSelfCertificationOrDefaults = InstitutionTaxSelfCertification | Partial<InstitutionTaxSelfCertification>;

export const INSTITUTION_ENTITY_TAX_RESIDENCY_DEFAULTS: InstitutionEntityTaxResidency = {
  jurisdiction: '',
  hasNoTin: false,
  tin: '',
  noTinReason: undefined,
  noTinExplanation: '',
};

const hasUniquePartyIds = (parties: Array<{ partyId: string }>) => new Set(parties.map(({ partyId }) => partyId)).size === parties.length;

const institutionEntityTaxResidencySchema = z
  .object({
    jurisdiction: mainstreamCountrySchema(i18n.t('institutionDetails.taxResidency.errors.jurisdiction')),
    hasNoTin: z.boolean(),
    tin: z.string(),
    noTinReason: z.enum(NoTinReason).optional(),
    noTinExplanation: z.string(),
  })
  .superRefine(async (residency, context) => {
    if (!residency.hasNoTin) {
      addFieldIssue(context, ['tin'], institutionDetailsV2EntityTinSchema, residency.tin);

      return;
    }

    // NL implementation (Besluit art. 3(2)): the no-TIN exception covers non-EU residences only
    if (await getCountryIsEu(residency.jurisdiction)) {
      context.addIssue({ code: 'custom', path: ['hasNoTin'], message: i18n.t('institutionDetails.taxResidency.errors.tinRequiredForEuResidence') });
    }

    if (!residency.noTinReason) {
      context.addIssue({ code: 'custom', path: ['noTinReason'], message: i18n.t('institutionDetails.taxResidency.errors.noTinReason') });
    }

    if (residency.noTinReason === NoTinReason.UNABLE_TO_OBTAIN) {
      addFieldIssue(context, ['noTinExplanation'], institutionDetailsV2NoTinExplanationSchema, residency.noTinExplanation);
    }
  });

export const institutionTaxSelfCertificationResidencySchema = z.discriminatedUnion(
  'noTaxResidence',
  [
    z.object({
      noTaxResidence: z.literal('NO'),
      taxResidencies: z
        .array(institutionEntityTaxResidencySchema)
        .min(1, i18n.t('institutionDetails.taxSelfCertification.errors.taxResidencies'))
        .max(TAX_RESIDENCIES_MAX, i18n.t('institutionDetails.taxSelfCertification.errors.taxResidenciesTooMany'))
        .refine((residencies) => new Set(residencies.map(({ jurisdiction }) => jurisdiction)).size === residencies.length, {
          error: i18n.t('institutionDetails.taxSelfCertification.errors.taxResidenciesDuplicate'),
          when: ({ issues }) => issues.length === 0,
        }),
    }),
    z.object({
      noTaxResidence: z.literal('YES'),
      placeOfEffectiveManagement: mainstreamCountrySchema(i18n.t('institutionDetails.taxSelfCertification.errors.placeOfEffectiveManagement')),
    }),
  ],
  { error: i18n.t('institutionDetails.taxSelfCertification.errors.noTaxResidence') },
);

export const institutionTaxSelfCertificationClassificationSchema = z.discriminatedUnion(
  'excludedPersonType',
  [
    z.object({
      excludedPersonType: z.enum([
        ExcludedPersonType.LISTED_CORPORATION,
        ExcludedPersonType.GOVERNMENTAL_ENTITY_INTERNATIONAL_ORGANISATION_OR_CENTRAL_BANK,
        ExcludedPersonType.FINANCIAL_INSTITUTION,
      ]),
    }),
    z.object({
      excludedPersonType: z.literal(ExcludedPersonType.RELATED_ENTITY_OF_LISTED_CORPORATION),
      listedEntityName: institutionDetailsV2ListedEntityNameSchema,
      listedMarket: institutionDetailsV2ListedMarketSchema,
    }),
    z.discriminatedUnion(
      'entityClassification',
      [
        z.object({
          excludedPersonType: z.literal(ExcludedPersonType.NONE),
          entityClassification: z.literal(EntityClassification.ACTIVE_ENTITY),
          activeEntityCriterion: institutionDetailsV2ActiveEntityCriterionSchema,
        }),
        z.object({
          excludedPersonType: z.literal(ExcludedPersonType.NONE),
          entityClassification: z.literal(EntityClassification.PASSIVE_ENTITY),
        }),
      ],
      { error: i18n.t('institutionDetails.taxSelfCertification.errors.entityClassification') },
    ),
  ],
  { error: i18n.t('institutionDetails.taxSelfCertification.errors.excludedPersonType') },
);

export const institutionTaxSelfCertificationControllingPersonsSchema = z.object({
  controllingPersons: z
    .array(
      // Contact details are not asked for, and only carried for people prefilled from the ownership section
      institutionPersonSchema.extend({
        email: z.string(),
        phoneNumber: z.object({ areaCode: z.string(), mobilePhone: z.string() }),
        controllingPersonTypes: z.array(z.enum(CONTROLLING_PERSON_TYPES)).min(1, i18n.t('institutionDetails.taxSelfCertification.errors.controllingPersonTypes')),
        isPep: institutionDetailsV2ControllingPersonSchema.shape.isPep,
      }),
    )
    .min(1, i18n.t('institutionDetails.taxSelfCertification.errors.controllingPersons'))
    .max(CONTROLLING_PERSONS_MAX, i18n.t('institutionDetails.taxSelfCertification.errors.controllingPersonsTooMany'))
    .refine(hasUniquePartyIds, { error: i18n.t('institutionDetails.taxSelfCertification.errors.duplicateParty'), when: ({ issues }) => issues.length === 0 }),
});

export const institutionTaxSelfCertificationDeclarationSchema = z.object({
  declaration: z.object({
    accurate: z.literal(true, { error: i18n.t('institutionDetails.taxSelfCertification.errors.declaration') }),
    consentToReporting: z.literal(true, { error: i18n.t('institutionDetails.taxSelfCertification.errors.declaration') }),
    undertakeToNotifyChanges: z.literal(true, { error: i18n.t('institutionDetails.taxSelfCertification.errors.declaration') }),
  }),
  signatory: z.object({
    partyId: z.uuid(i18n.t('institutionDetails.taxSelfCertification.errors.signatory')),
    fullName: z.string().min(1, i18n.t('institutionDetails.taxSelfCertification.errors.signatoryFullName')),
    capacity: z.string().min(1, i18n.t('institutionDetails.taxSelfCertification.errors.signatoryCapacity')),
  }),
});

export const isControllingPersonsApplicable = ({ excludedPersonType, entityClassification }: InstitutionTaxSelfCertificationOrDefaults) =>
  excludedPersonType === ExcludedPersonType.NONE && entityClassification === EntityClassification.PASSIVE_ENTITY;

const institutionEntityTaxResidencyToInstitutionDetailsEntityTaxResidencyApi = ({ jurisdiction, hasNoTin, tin, noTinReason, noTinExplanation }: InstitutionEntityTaxResidency) =>
  hasNoTin ? { jurisdiction, noTinReason, ...(noTinReason === NoTinReason.UNABLE_TO_OBTAIN && { noTinExplanation }) } : { jurisdiction, tin };

const institutionDetailsEntityTaxResidencyApiToInstitutionEntityTaxResidency = (residency: InstitutionDetailsV2EntityTaxResidency): InstitutionEntityTaxResidency => ({
  jurisdiction: residency.jurisdiction,
  hasNoTin: residency.noTinReason !== undefined,
  tin: residency.tin ?? '',
  noTinReason: residency.noTinReason,
  noTinExplanation: 'noTinExplanation' in residency ? residency.noTinExplanation : '',
});

const institutionControllingPersonToInstitutionDetailsControllingPersonApi = ({ controllingPersonTypes, isPep, ...person }: InstitutionControllingPerson) => ({
  ...institutionPersonToInstitutionDetailsPersonApi(person),
  controllingPersonTypes,
  isPep,
});

const institutionDetailsControllingPersonApiToInstitutionControllingPerson = ({
  controllingPersonTypes,
  isPep,
  ...person
}: InstitutionDetailsV2ControllingPerson): InstitutionControllingPerson => ({
  ...institutionDetailsPersonApiToInstitutionPerson({ ...person, email: '', phoneNumber: EMPTY_PHONE_NUMBER }),
  controllingPersonTypes,
  isPep,
});

// Parsing drops the answers left over from an earlier choice, and the contact details of people prefilled from the ownership section
export const institutionTaxSelfCertificationToInstitutionDetailsTaxSelfCertificationApi = (
  { taxResidencies, placeOfEffectiveManagement, controllingPersons, signatory, ...taxSelfCertification }: InstitutionTaxSelfCertification,
  date: Date,
): Promise<InstitutionDetailsV2TaxSelfCertification> =>
  institutionDetailsV2TaxSelfCertificationSchema.parseAsync({
    ...taxSelfCertification,
    taxResidencies: taxResidencies.map(institutionEntityTaxResidencyToInstitutionDetailsEntityTaxResidencyApi),
    placeOfEffectiveManagement: placeOfEffectiveManagement || undefined,
    controllingPersons: controllingPersons.map(institutionControllingPersonToInstitutionDetailsControllingPersonApi),
    signatory,
    date: date.toISOString(),
  });

export const institutionDetailsTaxSelfCertificationApiToInstitutionTaxSelfCertification = (
  taxSelfCertification: InstitutionDetailsV2TaxSelfCertification,
): InstitutionTaxSelfCertification => ({
  noTaxResidence: taxSelfCertification.noTaxResidence,
  taxResidencies: 'taxResidencies' in taxSelfCertification ? taxSelfCertification.taxResidencies.map(institutionDetailsEntityTaxResidencyApiToInstitutionEntityTaxResidency) : [],
  placeOfEffectiveManagement: 'placeOfEffectiveManagement' in taxSelfCertification ? taxSelfCertification.placeOfEffectiveManagement : '',
  excludedPersonType: taxSelfCertification.excludedPersonType,
  listedEntityName: 'listedEntityName' in taxSelfCertification ? taxSelfCertification.listedEntityName : '',
  listedMarket: 'listedMarket' in taxSelfCertification ? taxSelfCertification.listedMarket : '',
  entityClassification: 'entityClassification' in taxSelfCertification ? taxSelfCertification.entityClassification : undefined,
  activeEntityCriterion: 'activeEntityCriterion' in taxSelfCertification ? taxSelfCertification.activeEntityCriterion : undefined,
  controllingPersons:
    'controllingPersons' in taxSelfCertification ? taxSelfCertification.controllingPersons.map(institutionDetailsControllingPersonApiToInstitutionControllingPerson) : [],
  declaration: taxSelfCertification.declaration,
  signatory: taxSelfCertification.signatory,
});

// The UBOs, then the management who are not also UBOs, keeping each person's partyId. A UBO's role follows from how they own or control the company
export const getInstitutionControllingPersonCandidates = (ownership: InstitutionDetailsV2Partial['ownership']): InstitutionControllingPerson[] => {
  const owners = ownership && 'ubos' in ownership ? ownership.ubos : [];
  const management = (ownership?.management ?? []).filter(({ partyId }) => !owners.some((owner) => owner.partyId === partyId));

  return [
    ...owners.map(({ type, isPep, ...owner }) => ({
      ...institutionDetailsPersonApiToInstitutionPerson(owner),
      controllingPersonTypes: [type === BeneficialOwnerType.OWNER ? ControllingPersonType.OWNERSHIP_INTEREST : ControllingPersonType.SENIOR_MANAGING_OFFICIAL],
      isPep,
    })),
    ...management.map(({ roles, isPep, ...person }) => ({ ...institutionDetailsPersonApiToInstitutionPerson(person), controllingPersonTypes: [], isPep })),
  ];
};

// The first tax residence starts from the country of incorporation and the company's tax ID
export const createInstitutionTaxSelfCertificationDefaults = (answers: InstitutionDetailsV2Partial | null | undefined): InstitutionTaxSelfCertification => {
  return {
    taxResidencies: [
      {
        ...INSTITUTION_ENTITY_TAX_RESIDENCY_DEFAULTS,
        jurisdiction: answers?.incorporation?.countryOfIncorporation ?? '',
        tin: answers?.company?.taxId ?? '',
      },
    ],
    placeOfEffectiveManagement: '',
    listedEntityName: '',
    listedMarket: '',
    controllingPersons: [],
    declaration: { accurate: false, consentToReporting: false, undertakeToNotifyChanges: false },
    signatory: { fullName: '', capacity: '' },
  };
};
